/**
 * Versioned HTTP contract — `observability-v1` — for the observability platform
 * (`services/observability-api`, mounted same-origin at `/api/obs/*`). DEVOP-5 / ADR 0074.
 *
 * Same rules as the other `./http/*` contracts (ADR 0001/0005): the platform api and its
 * console must parse the SAME shapes without importing one another. This file is a thin
 * COMPOSITION of shapes that already exist — it REUSES `RunsOverview` (idea-engine queue
 * health, `./runs-v1.js`) and `AgentRun` (agent-runner, `../agent/domain.js`) rather than
 * re-declaring them, so the platform can never drift from the systems it observes. Reuses the
 * shared `{data,error,meta}` envelope (`./v1.js`); the namespace label below is the URL prefix,
 * not the envelope `meta.version` (which stays the shared `v1`). Breaking changes ship as
 * `observability-v2` alongside, never by mutation.
 *
 * Increment 2 (read-aggregation): the console reads ONE same-origin snapshot composed
 * server-side from the existing upstream endpoints. The durable ingestion shapes
 * (`TelemetryEvent` / `MetricPoint`) land in a later increment on this same file.
 */
import { z } from 'zod';
import { apiEnvelope } from './v1.js';
import { RunsOverviewSchema, RunWithTasksPayloadSchema } from './runs-v1.js';
import { AgentRunSchema, AgentRunEventSchema } from '../agent/domain.js';

/** Contract namespace (also the URL prefix `/api/obs/v1`). Envelope `meta.version` stays the shared `v1`. */
export const OBSERVABILITY_HTTP_CONTRACT_VERSION = 'observability-v1' as const;

/**
 * Derived agent-runner mini-overview. Agent-runner has no `/overview` endpoint (one
 * Container/DO per run, no shared queue), so the platform derives these counts from the
 * run list — the agent-side analogue of idea-engine's `RunsOverview` queue counts.
 */
export const AgentOverviewSchema = z.object({
  total: z.number().int().nonnegative(),
  queued: z.number().int().nonnegative(),
  running: z.number().int().nonnegative(),
  awaitingHuman: z.number().int().nonnegative(),
});
export type AgentOverview = z.infer<typeof AgentOverviewSchema>;

/**
 * One background-worker system's slice of the snapshot. `error` is a human-readable, non-null
 * string ONLY when that source degraded (unreachable/parse) — the snapshot is per-source
 * fault-tolerant: one upstream down never blanks the whole pane (AGENTS.md "no silent failures"
 * surfaces the degraded source, it does not hide it).
 */
export const IdeaEngineSliceSchema = z.object({
  /** The idea-engine run-queue overview (queue depth, stalls, oldest-queued age, recent runner events). */
  overview: RunsOverviewSchema.nullable(),
  error: z.string().nullable(),
});
export type IdeaEngineSlice = z.infer<typeof IdeaEngineSliceSchema>;

export const AgentRunnerSliceSchema = z.object({
  /** The owner's agent runs (newest-first), for the run list + derived counts. */
  runs: z.array(AgentRunSchema),
  derived: AgentOverviewSchema,
  error: z.string().nullable(),
});
export type AgentRunnerSlice = z.infer<typeof AgentRunnerSliceSchema>;

/**
 * One live snapshot across BOTH background-worker systems — the shape the console polls
 * (~2s, ADR 0051). `atMs` is the platform's stamp at composition time.
 */
export const ObservabilitySnapshotSchema = z.object({
  atMs: z.number().int().nonnegative(),
  ideaEngine: IdeaEngineSliceSchema,
  agentRunner: AgentRunnerSliceSchema,
});
export type ObservabilitySnapshot = z.infer<typeof ObservabilitySnapshotSchema>;

/** Request: read the current snapshot (empty body; the operator owner is server-configured). */
export const ObservabilitySnapshotRequestSchema = z.object({});
export type ObservabilitySnapshotRequest = z.infer<typeof ObservabilitySnapshotRequestSchema>;

/** Response: the snapshot, in the shared envelope. */
export const ObservabilitySnapshotResponseSchema = apiEnvelope(ObservabilitySnapshotSchema);
export type ObservabilitySnapshotResponse = z.infer<typeof ObservabilitySnapshotResponseSchema>;

// ── Durable ingestion (the unified sink) ─────────────────────────────────────
// The language-agnostic shape any producer POSTs to `/api/obs/internal/v1/ingest`. A
// `TelemetryEvent` GENERALISES `RunnerEvent` (runs-v1.ts) — `system` + `level` + `message` +
// free-form `attributes` on top of the same source/kind/ids — so it covers idea-engine runner
// events, agent step events, and future producers (data-lab, hr-engine) in one table. Ingestion
// is best-effort at the producer: low-volume lifecycle transitions, never per-poll/per-render.

export const TELEMETRY_LEVELS = ['debug', 'info', 'warn', 'error'] as const;
export const TelemetryLevelSchema = z.enum(TELEMETRY_LEVELS);
export type TelemetryLevel = z.infer<typeof TelemetryLevelSchema>;

const system = z.string().min(1).max(64);
const idRef = z.string().min(1).max(120);

/** One telemetry event as a producer SENDS it (the platform stamps `atMs` if absent). */
export const TelemetryEventInputSchema = z.object({
  /** Which background-worker system emitted it: `idea-engine` | `agent-runner` | … (open). */
  system,
  /** Where within that system: `gateway` | `do` | `worker` | `web` | `container` (open). */
  source: z.string().min(1).max(64),
  /** Lifecycle/step kind, e.g. `run.enqueued`, `cron.recycle`, `agent.step`, `drain.stop`. */
  kind: z.string().min(1).max(80),
  level: TelemetryLevelSchema.default('info'),
  ownerId: idRef.optional(),
  runId: idRef.optional(),
  taskId: idRef.optional(),
  correlationId: idRef.optional(),
  /** Human-readable feed line (generalises `RunnerEvent.detail`). */
  message: z.string().max(4000).optional(),
  /** Structured detail (generalises the agent step payload); parsed downstream, not here. */
  attributes: z.record(z.unknown()).default({}),
  /** Producer-supplied event time; the gateway stamps its own clock when absent. */
  atMs: z.number().int().nonnegative().optional(),
});
export type TelemetryEventInput = z.infer<typeof TelemetryEventInputSchema>;

/** One telemetry event as the durable feed RETURNS it (id + stamped `atMs`, newest-first). */
export const TelemetryEventSchema = z.object({
  id: z.string().min(1),
  system,
  source: z.string().min(1).max(64),
  kind: z.string().min(1).max(80),
  level: TelemetryLevelSchema,
  ownerId: idRef.nullable(),
  runId: idRef.nullable(),
  taskId: idRef.nullable(),
  correlationId: idRef.nullable(),
  message: z.string().max(4000).nullable(),
  attributes: z.record(z.unknown()),
  atMs: z.number().int().nonnegative(),
});
export type TelemetryEvent = z.infer<typeof TelemetryEventSchema>;

/** One numeric metric point (queue depth, failure rate, awaiting-human age) for alerting + trends. */
export const MetricPointInputSchema = z.object({
  system,
  /** e.g. `queue.depth` | `queue.oldest_ms` | `run.failed_rate` | `agent.awaiting_human_ms`. */
  name: z.string().min(1).max(120),
  value: z.number().finite(),
  unit: z.string().max(20).optional(),
  ownerId: idRef.optional(),
  labels: z.record(z.unknown()).default({}),
  atMs: z.number().int().nonnegative().optional(),
});
export type MetricPointInput = z.infer<typeof MetricPointInputSchema>;

/** One numeric metric point as the store RETURNS it (id + stamped `atMs`). */
export const MetricPointSchema = z.object({
  id: z.string().min(1),
  system,
  name: z.string().min(1).max(120),
  value: z.number().finite(),
  unit: z.string().max(20).nullable(),
  ownerId: idRef.nullable(),
  labels: z.record(z.unknown()),
  atMs: z.number().int().nonnegative(),
});
export type MetricPoint = z.infer<typeof MetricPointSchema>;

/** Request: a best-effort BATCH of events + metrics (internal, machine-to-machine, token-gated). */
export const TelemetryIngestRequestSchema = z.object({
  events: z.array(TelemetryEventInputSchema).max(500).default([]),
  metrics: z.array(MetricPointInputSchema).max(500).default([]),
});
export type TelemetryIngestRequest = z.infer<typeof TelemetryIngestRequestSchema>;

/** Response: how many of each landed. */
export const TelemetryIngestResponseSchema = apiEnvelope(
  z.object({ events: z.number().int().nonnegative(), metrics: z.number().int().nonnegative() }),
);
export type TelemetryIngestResponse = z.infer<typeof TelemetryIngestResponseSchema>;

// ── Browser span relay (A8 / IDEA-76) ────────────────────────────────────────
// The idea-engine WEB app cannot hold the platform ingest token (AGENTS.md: secrets stay
// server-side), so it batches its command + API-call spans and POSTs them to the gateway's
// authenticated `/v1/telemetry`, which forwards them to the platform via the SAME publisher.
// This is the browser→gateway request shape (reuses the producer input shapes). Bounded arrays
// (a browser batch is SMALL); the gateway also byte-caps the body and owner-stamps every span.

/** Max events/metrics one browser relay POST may carry (the gateway also byte-caps the body). */
export const TELEMETRY_RELAY_BATCH_MAX = 100;

export const TelemetryRelayRequestSchema = z.object({
  events: z.array(TelemetryEventInputSchema).max(TELEMETRY_RELAY_BATCH_MAX).default([]),
  metrics: z.array(MetricPointInputSchema).max(TELEMETRY_RELAY_BATCH_MAX).default([]),
});
export type TelemetryRelayRequest = z.infer<typeof TelemetryRelayRequestSchema>;

/** Response: how many spans the relay forwarded (best-effort — a forward may still no-op at noop). */
export const TelemetryRelayResponseSchema = apiEnvelope(
  z.object({ forwarded: z.object({ events: z.number().int().nonnegative(), metrics: z.number().int().nonnegative() }) }),
);
export type TelemetryRelayResponse = z.infer<typeof TelemetryRelayResponseSchema>;

// ── Durable feed (read) ──────────────────────────────────────────────────────

/**
 * Request: read the durable telemetry tail (newest-first). `system` scopes to one worker system;
 * `sinceMs`/`untilMs` bound the time window (the console's time-range selector); `beforeId` pages
 * further back (rows with a smaller id than the cursor). All optional — absent ⇒ the recent tail.
 */
export const TelemetryFeedRequestSchema = z.object({
  limit: z.number().int().positive().max(500).optional(),
  system: system.optional(),
  sinceMs: z.number().int().nonnegative().optional(),
  untilMs: z.number().int().nonnegative().optional(),
  beforeId: z.string().min(1).optional(),
});
export type TelemetryFeedRequest = z.infer<typeof TelemetryFeedRequestSchema>;

/** Response: the durable events, newest-first. */
export const TelemetryFeedResponseSchema = apiEnvelope(z.array(TelemetryEventSchema));
export type TelemetryFeedResponse = z.infer<typeof TelemetryFeedResponseSchema>;

// ── Per-run drill-down (read) ────────────────────────────────────────────────
// REUSE the systems' own detail shapes: an idea-engine run + its tasks; an agent run + its step
// log. The Live View URL is fetched server-side (Access-gated) and returned to the console for an
// embedded iframe (open-in-new-tab fallback if the third-party host refuses framing, ADR 0007).

/** Response: one idea-engine run + its tasks (null when absent/not the operator's). */
export const ObservabilityRunDetailResponseSchema = apiEnvelope(RunWithTasksPayloadSchema.nullable());
export type ObservabilityRunDetailResponse = z.infer<typeof ObservabilityRunDetailResponseSchema>;

/** One agent run + its ordered step log — the drill-down for a browser-automation run. */
export const AgentRunDetailSchema = z.object({
  run: AgentRunSchema,
  events: z.array(AgentRunEventSchema),
});
export type AgentRunDetail = z.infer<typeof AgentRunDetailSchema>;

/** Response: the agent run detail (null when absent/not the operator's). */
export const ObservabilityAgentRunDetailResponseSchema = apiEnvelope(AgentRunDetailSchema.nullable());
export type ObservabilityAgentRunDetailResponse = z.infer<typeof ObservabilityAgentRunDetailResponseSchema>;

/** Response: the run's live Browser Run view URL (null when none is available yet). */
export const ObservabilityLiveViewResponseSchema = apiEnvelope(z.object({ liveViewUrl: z.string().min(1) }).nullable());
export type ObservabilityLiveViewResponse = z.infer<typeof ObservabilityLiveViewResponseSchema>;

// ── Alerting (ADR 0074 §Decision 4) ──────────────────────────────────────────
// Threshold rules over the live signals (queue stalls, oldest-queued age, agent awaiting-human),
// evaluated by a pure function on a 1-min cron. A firing alert is deduped one-per-rule and surfaces
// in the console. `metric` names a signal extracted from the snapshot (e.g. `idea.stalledRunningTasks`).

export const ALERT_SEVERITIES = ['warn', 'critical'] as const;
export const AlertSeveritySchema = z.enum(ALERT_SEVERITIES);
export type AlertSeverity = z.infer<typeof AlertSeveritySchema>;

export const ALERT_COMPARATORS = ['gt', 'gte', 'lt', 'lte'] as const;
export const AlertComparatorSchema = z.enum(ALERT_COMPARATORS);
export type AlertComparator = z.infer<typeof AlertComparatorSchema>;

export const AlertRuleSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  /** The signal this rule watches, extracted from the snapshot (e.g. `idea.oldestQueuedTaskAgeMs`). */
  metric: z.string().min(1).max(80),
  comparator: AlertComparatorSchema,
  threshold: z.number().finite(),
  severity: AlertSeveritySchema,
  enabled: z.boolean(),
});
export type AlertRule = z.infer<typeof AlertRuleSchema>;

export const ALERT_STATUSES = ['firing', 'resolved'] as const;
export const AlertStatusSchema = z.enum(ALERT_STATUSES);
export type AlertStatus = z.infer<typeof AlertStatusSchema>;

export const AlertSchema = z.object({
  id: z.string().min(1),
  ruleId: z.string().min(1).max(64),
  severity: AlertSeveritySchema,
  status: AlertStatusSchema,
  metric: z.string().min(1).max(80),
  /** The observed value that (last) tripped/held the rule. */
  observed: z.number().finite(),
  /** Human-readable: what is wrong + what to do. */
  message: z.string().min(1).max(500),
  openedAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
  resolvedAt: z.number().int().nonnegative().nullable(),
});
export type Alert = z.infer<typeof AlertSchema>;

/** Response: current alerts (firing first, newest-first within a status). */
export const ObservabilityAlertsResponseSchema = apiEnvelope(z.array(AlertSchema));
export type ObservabilityAlertsResponse = z.infer<typeof ObservabilityAlertsResponseSchema>;

/** Response: the alert-eval outcome (how many rules fired / newly opened / resolved). */
export const AlertEvalResponseSchema = apiEnvelope(
  z.object({ evaluated: z.number().int().nonnegative(), firing: z.number().int().nonnegative(), opened: z.number().int().nonnegative(), resolved: z.number().int().nonnegative() }),
);
export type AlertEvalResponse = z.infer<typeof AlertEvalResponseSchema>;

// ── LLM cost & token observability (read) — ADR 0080 ─────────────────────────
// Producers emit ONE `llm.call` metric per model call (value = costUsd, unit = usd, labels =
// {model, provider, op, inputTokens, outputTokens}); the platform aggregates the recent window
// into this summary for the console cost pane. Aggregation is a pure function over metric points.

/** A cost roll-up for one dimension key (a service, a model, or an operation). */
export const CostGroupSchema = z.object({
  key: z.string(),
  usd: z.number().nonnegative(),
  calls: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
});
export type CostGroup = z.infer<typeof CostGroupSchema>;

/** The aggregated LLM spend over a window — totals + breakdowns by service, model, and operation. */
export const CostSummarySchema = z.object({
  atMs: z.number().int().nonnegative(),
  /** Span covered (ms) — `untilMs - sinceMs`, or the age of the oldest point when unbounded. */
  windowMs: z.number().int().nonnegative(),
  totalUsd: z.number().nonnegative(),
  totalCalls: z.number().int().nonnegative(),
  totalInputTokens: z.number().int().nonnegative(),
  totalOutputTokens: z.number().int().nonnegative(),
  /** Each breakdown is highest-spend-first. */
  byService: z.array(CostGroupSchema),
  byModel: z.array(CostGroupSchema),
  byOp: z.array(CostGroupSchema),
});
export type CostSummary = z.infer<typeof CostSummarySchema>;

/** Request: the LLM cost summary over an optional time window (absent ⇒ the recent retained window). */
export const CostsRequestSchema = z.object({
  sinceMs: z.number().int().nonnegative().optional(),
  untilMs: z.number().int().nonnegative().optional(),
});
export type CostsRequest = z.infer<typeof CostsRequestSchema>;

/** Response: the aggregated cost summary, in the shared envelope. */
export const ObservabilityCostsResponseSchema = apiEnvelope(CostSummarySchema);
export type ObservabilityCostsResponse = z.infer<typeof ObservabilityCostsResponseSchema>;
